import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { fetchNotificationDataDB } from '../../utils/apiUtils';
import { processNotificationTimestamp } from '../../utils/chartUtils';
import { getActuatorColor } from '../../utils/actionUtils';
import { usePairingMismatches, usePairingRepair } from '../actions/ActuatorPresence';
import PairingCheck from '../actions/PairingCheck';
import '../../styles/notificationsConsole.css';
import '../../styles/dashboardCards.css';

const NotificationsConsole = ({ 
  isOpen = false, 
  onToggle, 
  notifications = null, // Changed from mockNotifications to null
  maxHeight = "400px",
  hideButton = false
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartY, setDragStartY] = useState(0);
  const [notificationData, setNotificationData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [timeRange, setTimeRange] = useState(30); // Default to 30 days
  const contentRef = useRef(null);
  
  // Time range options
  const timeRangeOptions = [
    { label: '7 Days', value: 7 },
    { label: '30 Days', value: 30 },
    { label: '90 Days', value: 90 },
    { label: '6 Months', value: 180 },
    { label: '1 Year', value: 365 }
  ];
  
  // Filter states
  const [filters, setFilters] = useState({
    sensor: '',
    actuator: '',
    actuatorType: '',
    status: '',
    severity: '',
    searchText: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const pairingMismatches = usePairingMismatches();
  const { repairPairing, repairStatus } = usePairingRepair();

  // Fetch notifications when component mounts or when time range changes
  useEffect(() => {
    const fetchNotifications = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const data = await fetchNotificationDataDB(timeRange);
        // Only update data once we have new data - prevents flicker
        setNotificationData(data);
      } catch (err) {
        console.error('Failed to fetch notifications:', err);
        setError('Failed to load notifications');
      } finally {
        setIsLoading(false);
      }
    };

    // Only fetch if we don't have notifications passed as props
    if (!notifications) {
      fetchNotifications();
    }
  }, [notifications, timeRange]); // Add timeRange to dependencies

  // Auto-scroll to top when notifications change
  useEffect(() => {
    if (isOpen && contentRef.current) {
      contentRef.current.scrollTop = 0;
    }
  }, [isOpen, notificationData]);

  // Use provided notifications or fetched data
  const allNotifications = notifications || notificationData;

  // Get unique values for filter dropdowns
  const getUniqueValues = (key) => {
    const values = allNotifications.map(n => n[key]).filter(Boolean);
    return [...new Set(values)].sort();
  };

  const uniqueSensors = getUniqueValues('sensor_description');
  const uniqueActuators = getUniqueValues('actuator_description');
  const uniqueActuatorTypes = getUniqueValues('actuator_type');
  const uniqueStatuses = getUniqueValues('status');
  const uniqueSeverities = getUniqueValues('severity');

  // Filter notifications based on active filters
  const filterNotifications = (notifications) => {
    return notifications.filter(notification => {
      // Sensor filter
      if (filters.sensor && notification.sensor_description !== filters.sensor) {
        return false;
      }
      
      // Actuator filter
      if (filters.actuator && notification.actuator_description !== filters.actuator) {
        return false;
      }
      
      // Actuator Type filter
      if (filters.actuatorType && notification.actuator_type !== filters.actuatorType) {
        return false;
      }
      
      // Status filter
      if (filters.status && notification.status !== filters.status) {
        return false;
      }
      
      // Severity filter
      if (filters.severity && notification.severity !== filters.severity) {
        return false;
      }
      
      // Search text filter (searches in message, sensor, and actuator)
      if (filters.searchText) {
        const searchLower = filters.searchText.toLowerCase();
        const matchesSearch = 
          (notification.message && notification.message.toLowerCase().includes(searchLower)) ||
          (notification.sensor_description && notification.sensor_description.toLowerCase().includes(searchLower)) ||
          (notification.actuator_description && notification.actuator_description.toLowerCase().includes(searchLower));
        
        if (!matchesSearch) {
          return false;
        }
      }
      
      return true;
    });
  };

  // Apply filters and display
  const displayNotifications = filterNotifications(allNotifications);

  // Check if any filters are active
  const hasActiveFilters = Object.values(filters).some(value => value !== '');

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      sensor: '',
      actuator: '',
      actuatorType: '',
      status: '',
      severity: '',
      searchText: ''
    });
  };

  // Update individual filter
  const updateFilter = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  // Format timestamp for display using CST timezone
  const formatTimestamp = (timestamp) => {
    try {
      return processNotificationTimestamp(timestamp);
    } catch (error) {
      return timestamp;
    }
  };

  // Get severity color class
  const getSeverityClass = (severity) => {
    switch (severity?.toLowerCase()) {
      case 'critical':
        return 'severity-critical';
      case 'warning':
        return 'severity-warning';
      case 'info':
      default:
        return 'severity-info';
    }
  };

  // Get status color class
  const getStatusClass = (status) => {
    switch (status?.toUpperCase()) {
      case 'ON':
        return 'status-on';
      case 'OFF':
        return 'status-off';
      case 'TRIGGERED':
        return 'status-triggered';
      default:
        return 'status-default';
    }
  };

  // Handle drag start for potential future drag-to-close functionality
  const handleDragStart = (e) => {
    if (e.target.closest('.notifications-table') || e.target.closest('.notifications-header')) {
      return; // Don't start drag if clicking on table or header
    }
    setIsDragging(true);
    setDragStartY(e.clientY);
  };

  const handleDragEnd = () => {
    setIsDragging(false);
  };

  const handleDragMove = (e) => {
    if (!isDragging) return;
    
    const deltaY = e.clientY - dragStartY;
    if (deltaY > 100) { // If dragged down more than 100px, close
      onToggle();
      setIsDragging(false);
    }
  };

  return (
    <div className="notifications-console-container">
      {/* Toggle Button - Only show when panel is closed and other modals are not open */}
      {!isOpen && !hideButton && (
        <button 
          className="notifications-toggle-button"
          onClick={onToggle}
          aria-label={
            pairingMismatches.length > 0
              ? `Open notifications, ${pairingMismatches.length} pairing check${pairingMismatches.length === 1 ? '' : 's'}`
              : 'Open notifications'
          }
          title="Open notifications"
        >
          <i className="ri-notification-3-line"></i>
          {pairingMismatches.length > 0 && (
            <span className="notification-badge notification-badge-pairing">{pairingMismatches.length}</span>
          )}
          {displayNotifications.length > 0 && (
            <span className="notification-badge">{displayNotifications.length}</span>
          )}
        </button>
      )}

      {/* Console Panel */}
      <div 
        className={`notifications-panel ${isOpen ? 'open' : ''}`}
        style={{ maxHeight: isOpen ? maxHeight : '0px' }}
        onMouseDown={handleDragStart}
        onMouseUp={handleDragEnd}
        onMouseLeave={handleDragEnd}
        onMouseMove={handleDragMove}
      >
        {/* Header */}
        <div className="notifications-header">
          <div className="notifications-header-content">
            <h3 className="notifications-title">
              <i className="ri-notification-3-line"></i>
              Notifications
            </h3>
            <div className="notifications-header-actions">
              {/* Time Range Selector */}
              <select 
                className="notifications-time-range-select"
                value={timeRange}
                onChange={(e) => setTimeRange(Number(e.target.value))}
                title="Select time range"
              >
                {timeRangeOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <button 
                className="notifications-filter-toggle-btn"
                onClick={() => setShowFilters(!showFilters)}
                aria-label="Toggle filters"
                title={showFilters ? "Hide filters" : "Show filters"}
              >
                <i className={`ri-filter-${showFilters ? 'fill' : 'line'}`}></i>
                {hasActiveFilters && <span className="filter-active-indicator"></span>}
              </button>
              <span className="notifications-count">
                {displayNotifications.length} {hasActiveFilters ? 'filtered' : 'of'} {allNotifications.length}
              </span>
              <button 
                className="notifications-close-btn"
                onClick={onToggle}
                aria-label="Close notifications"
              >
                <i className="ri-close-line"></i>
              </button>
            </div>
          </div>
          <div className="notifications-drag-handle">
            <div className="drag-indicator"></div>
          </div>
        </div>

        <div className="notifications-pairing-section">
          <PairingCheck
            mismatches={pairingMismatches}
            repairStatus={repairStatus}
            onRepair={repairPairing}
          />
        </div>

        {/* Filters Section */}
        {showFilters && (
          <div className="notifications-filters">
            <div className="filters-row">
              <div className="filter-group">
                <label htmlFor="filter-sensor">
                  <i className="ri-dashboard-line"></i> Sensor/Tray
                </label>
                <select
                  id="filter-sensor"
                  value={filters.sensor}
                  onChange={(e) => updateFilter('sensor', e.target.value)}
                  className="filter-select"
                >
                  <option value="">All Sensors</option>
                  {uniqueSensors.map(sensor => (
                    <option key={sensor} value={sensor}>{sensor}</option>
                  ))}
                </select>
              </div>

              <div className="filter-group">
                <label htmlFor="filter-actuator">
                  <i className="ri-settings-line"></i> Actuator
                </label>
                <select
                  id="filter-actuator"
                  value={filters.actuator}
                  onChange={(e) => updateFilter('actuator', e.target.value)}
                  className="filter-select"
                >
                  <option value="">All Actuators</option>
                  {uniqueActuators.map(actuator => (
                    <option key={actuator} value={actuator}>{actuator}</option>
                  ))}
                </select>
              </div>

              <div className="filter-group">
                <label htmlFor="filter-type">
                  <i className="ri-price-tag-3-line"></i> Type
                </label>
                <select
                  id="filter-type"
                  value={filters.actuatorType}
                  onChange={(e) => updateFilter('actuatorType', e.target.value)}
                  className="filter-select"
                >
                  <option value="">All Types</option>
                  {uniqueActuatorTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              <div className="filter-group">
                <label htmlFor="filter-status">
                  <i className="ri-toggle-line"></i> Status
                </label>
                <select
                  id="filter-status"
                  value={filters.status}
                  onChange={(e) => updateFilter('status', e.target.value)}
                  className="filter-select"
                >
                  <option value="">All Statuses</option>
                  {uniqueStatuses.map(status => (
                    <option key={status} value={status}>{status}</option>
                  ))}
                </select>
              </div>

              <div className="filter-group">
                <label htmlFor="filter-severity">
                  <i className="ri-alert-line"></i> Severity
                </label>
                <select
                  id="filter-severity"
                  value={filters.severity}
                  onChange={(e) => updateFilter('severity', e.target.value)}
                  className="filter-select"
                >
                  <option value="">All Severities</option>
                  {uniqueSeverities.map(severity => (
                    <option key={severity} value={severity}>{severity}</option>
                  ))}
                </select>
              </div>

              <div className="filter-group filter-search">
                <label htmlFor="filter-search">
                  <i className="ri-search-line"></i> Search
                </label>
                <div className="filter-search-row">
                  <input
                    id="filter-search"
                    type="text"
                    value={filters.searchText}
                    onChange={(e) => updateFilter('searchText', e.target.value)}
                    placeholder="Search messages..."
                    className="filter-input"
                  />
                  {hasActiveFilters && (
                    <button 
                      className="clear-filters-btn"
                      onClick={clearFilters}
                      title="Clear all active filters"
                    >
                      <i className="ri-close-circle-line"></i>
                      Clear All
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Content with improved scrolling */}
        <div 
          className="notifications-content"
          ref={contentRef}
          tabIndex={0} // Make it focusable for keyboard navigation
        >
          {/* Loading overlay - shows on top of existing data to prevent flicker */}
          {isLoading && (
            <div className="notifications-loading-overlay">
              <div className="notifications-loading-content">
              <i className="ri-loader-4-line"></i>
              <p>Loading notifications...</p>
              </div>
            </div>
          )}
          
          {/* Error message */}
          {error && !isLoading && (
            <div className="notifications-error">
              <i className="ri-error-warning-line"></i>
              <p>{error}</p>
            </div>
          )}
          
          {/* Table - always show, even while loading (prevents flicker) */}
          {(!error || isLoading) && (
            <div className={`notifications-table-container ${isLoading ? 'loading' : ''}`}>
              <table className="notifications-table">
                <thead>
                  <tr>
                    <th>⏱️ Time</th>
                    <th>📡 Sensor</th>
                    <th>⚙️ Actuator</th>
                    <th>🏷️ Type</th>
                    <th>📊 Status</th>
                    <th>📈 Proportion</th>
                    <th>⚠️ Severity</th>
                    <th>💬 Message</th>
                  </tr>
                </thead>
                <tbody>
                  {displayNotifications.length > 0 ? (
                    displayNotifications.map((notification, index) => (
                      <tr key={index} className="notification-row">
                        <td className="notification-time">
                          {formatTimestamp(notification.timestamp_utc)}
                        </td>
                        <td className="notification-sensor">
                          {notification.sensor_description}
                        </td>
                        <td className="notification-actuator">
                          {notification.actuator_description}
                        </td>
                        <td className="notification-type">
                          <span 
                            className="actuator-type-badge"
                            style={{ 
                              backgroundColor: getActuatorColor(notification.actuator_type),
                              color: 'white',
                              padding: '4px 8px',
                              borderRadius: '12px',
                              fontSize: '12px',
                              fontWeight: '600',
                              textTransform: 'capitalize'
                            }}
                          >
                            {notification.actuator_type}
                          </span>
                        </td>
                        <td className={`notification-status ${getStatusClass(notification.status)}`}>
                          {notification.status}
                        </td>
                        <td className="notification-proportion">
                          {notification.proportion !== null && notification.proportion !== undefined 
                            ? `${(notification.proportion * 100).toFixed(0)}%` 
                            : '-'}
                        </td>
                        <td className={`notification-severity ${getSeverityClass(notification.severity)}`}>
                          {notification.severity}
                        </td>
                        <td className="notification-message">
                          {notification.message || '-'}
                        </td>
                      </tr>
                    ))
                  ) : (
                    !isLoading && (
                    <tr className="notifications-empty-row">
                      <td colSpan="8" className="notifications-empty-cell">
                        <div className="notifications-empty">
                          <i className="ri-notification-off-line"></i>
                          <p>No recent notifications</p>
                          <small>Notifications will appear here when they occur</small>
                        </div>
                      </td>
                    </tr>
                    )
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

NotificationsConsole.propTypes = {
  isOpen: PropTypes.bool,
  onToggle: PropTypes.func.isRequired,
  notifications: PropTypes.arrayOf(PropTypes.shape({
    timestamp_utc: PropTypes.string.isRequired,
    sensor_description: PropTypes.string.isRequired,
    actuator_description: PropTypes.string.isRequired,
    actuator_type: PropTypes.string.isRequired,
    status: PropTypes.string.isRequired,
    proportion: PropTypes.number,
    severity: PropTypes.string.isRequired,
    message: PropTypes.string,
    note_id: PropTypes.number,
    user_id: PropTypes.string,
    mac_address: PropTypes.string,
    username: PropTypes.string
  })),
  maxHeight: PropTypes.string,
  hideButton: PropTypes.bool
};

export default NotificationsConsole; 